/** Research guide §4 — The AI chain (`research/ResearchAiChainView.tsx`). */
import { helpSectionMeta } from '../help-sections.js';
import {
  Btn,
  Bullets,
  Callout,
  HelpSectionCard,
  Li,
  P,
  SectionLink,
  Shot,
  SubHead,
} from '../HelpPrimitives.js';

export function ResearchAiChain(): React.JSX.Element {
  return (
    <HelpSectionCard meta={helpSectionMeta('research-aichain')}>
      <Shot
        slug="research-aichain"
        caption="The AI chain screen: nothing checked yet — 0 companies selected."
      />
      <Callout title="Paid, manual, per company">
        <P>
          Nothing on this screen is checked until you select companies and press the button — the
          screen says so at the top. There is no batch-wide auto-run, and no timer that starts a
          check for you.
        </P>
      </Callout>

      <SubHead>Selecting companies</SubHead>
      <Bullets>
        <Li>
          <Btn>Select first N</Btn> — pick a number (default 10, up to 50) and select that many
          long-list rows at once.
        </Li>
        <Li>
          <Btn>Select all</Btn> — a single checkbox for everything currently on screen.
        </Li>
        <Li>Or tick individual rows yourself.</Li>
      </Bullets>

      <SubHead>Running the check</SubHead>
      <Bullets>
        <Li>
          <Btn>Run AI check (N selected)</Btn> opens a confirmation dialog that names the exact
          number of companies before anything runs.
        </Li>
        <Li>
          Per row, <Btn>Run AI</Btn> / <Btn>Re-check</Btn> and <Btn>Retry</Btn> check one company at
          a time — each its own click, no confirmation needed for a single row.
        </Li>
        <Li>
          <Btn>Sources</Btn> is free — it just opens the pages already collected for that company.
        </Li>
      </Bullets>

      <SubHead>What actually happens on a check</SubHead>
      <P>
        The app downloads the company&rsquo;s own website with a plain page fetch — no search
        engine, no Anthropic web search tool anywhere in this pipeline — has the AI pick a handful
        of relevant pages (About, Team, Careers, Products), downloads those too, then has the AI
        read the collected text to extract facts and verify the company against your request. You
        can always open <Btn>Sources</Btn> afterwards to see exactly which pages a company&rsquo;s
        result came from.
      </P>

      <P>
        Once you&rsquo;re done checking, continue to{' '}
        <SectionLink to="research-review">Reviewing the long-list</SectionLink>.
      </P>
    </HelpSectionCard>
  );
}
